import { useState } from "react";
import { motion } from "framer-motion";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { TrendingUp, TrendingDown, Zap, AlertCircle, Info, CheckCircle } from "lucide-react";
import { MONTHLY_DATA, CAMPAIGN_PERFORMANCE, LEAD_SOURCES, TEAM_PERFORMANCE, FUNNEL_DATA, AI_INSIGHTS } from "@/data/dummyData";

const BLUE_PALETTE = ["#2563eb", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#dbeafe", "#eff6ff"];

const insightConfig = {
  opportunity: { icon: Info, classes: "bg-blue-50 border-blue-200 text-blue-700" },
  warning: { icon: AlertCircle, classes: "bg-yellow-50 border-yellow-200 text-yellow-700" },
  success: { icon: CheckCircle, classes: "bg-emerald-50 border-emerald-200 text-emerald-700" },
};

function SectionCard({ title, subtitle, children, testId }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid={testId}>
      <div className="mb-5">
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export default function Analytics() {
  const [period, setPeriod] = useState("6m");

  const totalLeads = MONTHLY_DATA.reduce((s, d) => s + d.leads, 0);
  const totalBookings = MONTHLY_DATA.reduce((s, d) => s + d.bookings, 0);
  const totalRevenue = MONTHLY_DATA.reduce((s, d) => s + d.revenue, 0);
  const conversionRate = ((totalBookings / totalLeads) * 100).toFixed(1);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Analytics</h2>
          <p className="text-slate-500 text-sm mt-0.5">Business performance overview · Elite Estate</p>
        </div>
        <div className="flex gap-2">
          {["1m", "3m", "6m", "1y"].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${period === p ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}
              data-testid={`period-${p}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Leads (6mo)", value: totalLeads.toLocaleString(), change: "+24%", up: true, color: "text-blue-600" },
          { label: "Bookings", value: totalBookings, change: "+41%", up: true, color: "text-emerald-600" },
          { label: "Conversion Rate", value: `${conversionRate}%`, change: "+3.2%", up: true, color: "text-purple-600" },
          { label: "Revenue Pipeline", value: `₹${totalRevenue.toFixed(1)} Cr`, change: "+56%", up: true, color: "text-orange-600" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            className="bg-white rounded-xl border border-slate-200 p-5"
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
          >
            <p className="text-xs text-slate-500 mb-2">{stat.label}</p>
            <p className={`font-heading text-2xl font-bold ${stat.color}`}>{stat.value}</p>
            <div className="flex items-center gap-1 mt-1">
              {stat.up ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : <TrendingDown className="w-3 h-3 text-red-500" />}
              <span className={`text-xs font-semibold ${stat.up ? "text-emerald-600" : "text-red-500"}`}>{stat.change} vs last period</span>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Lead Funnel */}
      <SectionCard title="Lead Conversion Funnel" subtitle="Full journey: Ad Click → Booking" testId="lead-funnel-chart">
        <div className="space-y-3">
          {FUNNEL_DATA.map((stage, i) => (
            <div key={stage.stage} className="flex items-center gap-4">
              <div className="w-32 text-xs font-medium text-slate-600 text-right shrink-0">{stage.stage}</div>
              <div className="flex-1 bg-slate-100 rounded-full h-7 overflow-hidden relative">
                <motion.div
                  className="h-full rounded-full flex items-center pl-3"
                  style={{ background: `hsl(${221 - i * 8}, ${83 - i * 5}%, ${53 + i * 5}%)` }}
                  initial={{ width: 0 }}
                  animate={{ width: `${stage.pct}%` }}
                  transition={{ duration: 0.8, delay: i * 0.1 }}
                >
                  <span className="text-xs font-bold text-white whitespace-nowrap">{stage.count.toLocaleString()}</span>
                </motion.div>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500">{stage.pct}%</span>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Two Chart Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Lead & Booking Trend */}
        <SectionCard title="Monthly Lead Trend" subtitle="Leads, qualified, and bookings over time" testId="monthly-trend-chart">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MONTHLY_DATA} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gradLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }} />
              <Area type="monotone" dataKey="leads" stroke="#2563eb" strokeWidth={2} fill="url(#gradLeads)" name="Leads" />
              <Area type="monotone" dataKey="qualified" stroke="#60a5fa" strokeWidth={1.5} fill="none" strokeDasharray="4 2" name="Qualified" />
              <Area type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={2} fill="none" name="Bookings" />
            </AreaChart>
          </ResponsiveContainer>
        </SectionCard>

        {/* Lead Sources */}
        <SectionCard title="Lead Sources" subtitle="Breakdown by acquisition channel" testId="lead-sources-chart">
          <div className="flex items-center gap-6">
            <ResponsiveContainer width="50%" height={200}>
              <PieChart>
                <Pie data={LEAD_SOURCES} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" strokeWidth={2}>
                  {LEAD_SOURCES.map((entry, i) => (
                    <Cell key={i} fill={BLUE_PALETTE[i]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }} formatter={(v) => [`${v}%`, ""]} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-3">
              {LEAD_SOURCES.map((s, i) => (
                <div key={s.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: BLUE_PALETTE[i] }} />
                    <span className="text-sm text-slate-600">{s.name}</span>
                  </div>
                  <span className="text-sm font-bold text-slate-800">{s.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Campaign Performance */}
      <SectionCard title="Campaign Performance" subtitle="Leads generated and qualified per channel" testId="campaign-performance-chart">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={CAMPAIGN_PERFORMANCE} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }} />
            <Legend wrapperStyle={{ fontSize: "11px" }} />
            <Bar dataKey="leads" fill="#2563eb" radius={[4, 4, 0, 0]} name="Total Leads" />
            <Bar dataKey="qualified" fill="#60a5fa" radius={[4, 4, 0, 0]} name="Qualified" />
          </BarChart>
        </ResponsiveContainer>
      </SectionCard>

      {/* Team Performance + AI Insights Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Team Performance */}
        <SectionCard title="Team Performance" subtitle="Sales team results this month" testId="team-performance-chart">
          <div className="space-y-3">
            {TEAM_PERFORMANCE.map((member, i) => (
              <div key={member.name} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  {member.name.split(" ").map(n => n[0]).join("")}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-semibold text-slate-800">{member.name}</p>
                    <span className="text-xs font-bold text-emerald-600">₹{member.revenue} Cr</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{member.leads} leads</span>
                    <span>{member.qualified} qualified</span>
                    <span className="font-semibold text-blue-600">{member.bookings} bookings</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-1.5 mt-1.5">
                    <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: `${(member.bookings / 10) * 100}%` }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>

        {/* AI Insights */}
        <SectionCard title="AI Insights" subtitle="Intelligent recommendations for your business" testId="ai-insights-analytics">
          <div className="space-y-3">
            {AI_INSIGHTS.map(insight => {
              const { icon: Icon, classes } = insightConfig[insight.type];
              return (
                <div key={insight.id} className={`p-3.5 rounded-xl border ${classes}`}>
                  <div className="flex items-start gap-2.5">
                    <Icon className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold mb-1">{insight.title}</p>
                      <p className="text-xs opacity-80 leading-relaxed">{insight.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <button className="text-xs font-semibold underline underline-offset-2 hover:opacity-70">{insight.action}</button>
                        <span className="text-xs opacity-60">{insight.time}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>
      </div>

      {/* Revenue Forecast */}
      <SectionCard title="Revenue Forecast" subtitle="Monthly bookings and pipeline revenue (Cr)" testId="revenue-forecast-chart">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={MONTHLY_DATA} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "11px" }} formatter={(v) => [`₹${v} Cr`, ""]} />
            <Area type="monotone" dataKey="revenue" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#gradRevenue)" name="Revenue" />
          </AreaChart>
        </ResponsiveContainer>
      </SectionCard>
    </div>
  );
}
